import { Injectable, OnDestroy } from '@angular/core';
import { LatLng } from 'leaflet';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, switchMap, take, takeUntil } from 'rxjs/operators';
import { MapEventType } from 'src/app/map/model/map-event';
import { MapEventService } from 'src/app/map/service/map-event.service';

@Injectable({
  providedIn: 'root'
})
export class ChatMapBridgeService implements OnDestroy {
  readonly mapSelectionKey: string = 'chat-drawing-polygon';

  private mapServiceSub = new BehaviorSubject<MapEventService | null>(null);
  private activeServiceSub = new BehaviorSubject<MapEventService | null>(null);
  private unsubscribe = new Subject<void>();
  private mainSub: Subscription;

  constructor() {
    this.mainSub = this.mapServiceSub
      .asObservable()
      .pipe(distinctUntilChanged(), pairwise())
      .subscribe({
        next: ([oldS, newS]) => {
          if (newS == null || newS != null && oldS != null && oldS !== newS) {
            this.stopServiceDraw(oldS);
            this.unsubscribe.next();
          }
          this.activeServiceSub.next(newS);
        }
      });
  }

  ngOnDestroy(): void {
    this.mainSub.unsubscribe();
    this.unsubscribe.next();
  }

  get isActive(): boolean {
    return this.activeServiceSub.value != null;
  }

  init(service: MapEventService): void {
    this.mapServiceSub.next(service);
  }

  disconnect(): void {
    this.mapServiceSub.next(null);
  }

  draw(): Observable<LatLng[]> {
    return this.activeServiceSub.asObservable().pipe(
      filter((s) => s != null),
      take(1),
      switchMap((service) => {
        service.enableDrawPolygon(this.mapSelectionKey);
        return this.pointsObs$(service);
      }),
      takeUntil(this.unsubscribe)
    );
  }

  stopDraw(): void {
    this.activeServiceSub
      .asObservable()
      .pipe(
        filter((s) => s != null),
        take(1)
      )
      .subscribe({
        next: (service) => this.stopServiceDraw(service)
      });
  }

  disconnectSub(): Observable<void> {
    return this.unsubscribe.asObservable();
  }

  private pointsObs$(service: MapEventService): Observable<LatLng[]> {
    return service.events$.pipe(
      filter((event) => event.type === MapEventType.SELECTED_POLYGON && event.data.key === this.mapSelectionKey),
      map((event) => event.data?.points as LatLng[])
    );
  }

  private stopServiceDraw(service?: MapEventService | null): void {
    service?.clearPolygon(this.mapSelectionKey);
    service?.cancelDrawPolygon(this.mapSelectionKey);
  }
}
